import { z } from "zod";
/**
 * How a quote is paid, as the sales team agrees it with the customer:
 * - reservation_fee: a fixed amount that holds the date; it counts toward the total and is non-refundable
 *   (school visits); the total is paid when the balance is due.
 * - advance: part of the total to reserve (tours, usually 50%); the rest is due later.
 * - on_service_day: nothing now, everything on the day.
 * - full_now: everything when reserving.
 */
export const PAYMENT_TERM_KINDS = ["reservation_fee", "advance", "on_service_day", "full_now"];
export const paymentTermsSchema = z
    .object({
    kind: z.enum(PAYMENT_TERM_KINDS),
    upfront: z.number().nonnegative().max(10_000_000).default(0),
    balance_due: z.enum(["service_day", "day_before"]).default("service_day"),
})
    .strict();
export const paymentTermLabels = {
    reservation_fee: "Apartado de fecha (se descuenta del total, no reembolsable)",
    advance: "Anticipo y el resto antes del servicio",
    on_service_day: "Todo el día del servicio",
    full_now: "Pago completo al reservar",
};
/** Sensible starting terms per service: schools hold the date, tours pay 50% and settle a day before. */
export function defaultPaymentTerms(service, total = 0) {
    if (service === "school")
        return { kind: "reservation_fee", upfront: 0, balance_due: "service_day" };
    if (service === "tour")
        return { kind: "advance", upfront: Math.round(total / 2), balance_due: "day_before" };
    return { kind: "advance", upfront: Math.round(total / 2), balance_due: "service_day" };
}
/** Amount paid now, the rest and when the rest is due, for documents and reminders. */
export function paymentSchedule(terms, total, serviceDate, prepaid = 0) {
    const kind = terms?.kind ?? "full_now";
    // Money received before the quote (a transfer agreed by message): nothing more to reserve, only the rest.
    const paid = Math.min(total, Math.max(0, prepaid));
    const upfront = paid > 0 ? 0 : kind === "on_service_day" ? 0 : kind === "full_now" ? total : Math.max(0, terms?.upfront ?? 0);
    // A reservation fee and an advance are both part of the total; the fee is just not refundable.
    const rest = paid > 0 ? Math.round((total - paid) * 100) / 100 : kind === "reservation_fee" || kind === "advance" ? Math.max(0, total - upfront) : kind === "on_service_day" ? total : 0;
    const dueDate = rest > 0 && serviceDate && /^\d{4}-\d{2}-\d{2}$/.test(serviceDate)
        ? terms?.balance_due === "day_before"
            ? new Date(Date.parse(`${serviceDate}T12:00:00Z`) - 86_400_000).toISOString().slice(0, 10)
            : serviceDate
        : null;
    return { kind, upfront, rest, prepaid: paid, dueDate, dayBefore: terms?.balance_due === "day_before" };
}
const reminderServices = { tour: "tour", school: "visita escolar", balloon: "vuelo en globo" };
const ddmmyyyy = (date) => date.slice(0, 10).split("-").reverse().join("/");
/** WhatsApp text that reminds the customer to settle the balance before the service. */
export function balanceReminderMessage(input) {
    const first = input.service === "school" ? input.customerName.trim() : input.customerName.trim().split(/\s+/)[0] ?? "";
    const amount = new Intl.NumberFormat("es-MX", { style: "currency", currency: input.currency || "MXN" }).format(input.amount);
    const service = reminderServices[input.service] ?? "servicio";
    const when = input.serviceDate ? ` del ${ddmmyyyy(input.serviceDate)}` : "";
    const due = input.dueDate ? ` a más tardar el ${ddmmyyyy(input.dueDate)}` : " antes del servicio";
    return `Hola${first ? ` ${first}` : ""}, te recordamos que para tu ${service}${when} queda pendiente un saldo de ${amount}. Es necesario liquidarlo${due}, por transferencia o en efectivo. Si ya lo pagaste, envíanos tu comprobante por aquí. ¡Gracias!`;
}
/** A discount the seller gives on a quote: a percentage (e.g. 10%) or a fixed amount (e.g. $200). */
export const quoteDiscountSchema = z
    .object({
    type: z.enum(["percent", "amount"]),
    value: z.number().positive().max(10_000_000),
})
    .strict()
    .refine((d) => d.type !== "percent" || d.value < 100, { message: "El porcentaje debe ser menor a 100." });
/** Amount taken off the total, rounded to cents and never the whole total. Same rule as WordPress. */
export function discountAmount(discount, total) {
    if (!discount || !(discount.value > 0) || !(total > 0))
        return 0;
    const raw = discount.type === "percent" ? (total * Math.min(discount.value, 99.99)) / 100 : discount.value;
    return Math.round(Math.min(raw, total - 1) * 100) / 100;
}
/** "Descuento 10%" or "Descuento". */
export const discountLabel = (discount) => discount.type === "percent" ? `Descuento ${discount.value}%` : "Descuento";
